import { useEffect, useState } from "react";
import { ChevronDown, History } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { fmtINR, fmtDate, STATUS_STYLES, STATUS_LABELS } from "@/lib/format";

const SUMMARY_LABELS = [
  ["nights", "nights boarded"],
  ["day_care_days", "day care days"],
  ["grooming", "grooming sessions"],
  ["training", "training sessions"],
  ["vaccinations", "vaccinations"],
  ["events", "events"],
];

const Entry = ({ entry }) => {
  const [open, setOpen] = useState(false);
  return (
    <li className="rounded-2xl border border-ink/8 bg-white">
      <button
        type="button"
        data-testid={`history-entry-${entry.booking_id}`}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-3 p-4 text-left"
      >
        <div className="flex items-center gap-3">
          <span className={`rounded-full px-3 py-1 text-[11px] font-semibold ${STATUS_STYLES[entry.status]}`}>
            {STATUS_LABELS[entry.status]}
          </span>
          <span className="text-sm font-medium text-ink">
            {entry.items.map((i) => i.service_name).join(" · ")}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-400">{fmtDate(entry.items[0]?.start_date)}</span>
          <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`} aria-hidden="true" />
        </div>
      </button>
      {open && (
        <ul className="space-y-1.5 border-t border-ink/8 p-4">
          {entry.items.map((i, idx) => (
            <li key={idx} className="flex justify-between text-sm">
              <span className="text-slate-600">
                {i.service_name} · {fmtDate(i.start_date)}
                {i.end_date !== i.start_date && ` → ${fmtDate(i.end_date)}`}
                {i.days > 1 && ` · ${i.days} ${i.unit}s`}
              </span>
              <span className="font-semibold text-ink">{fmtINR(i.amount)}</span>
            </li>
          ))}
          <li className="flex justify-between border-t border-ink/8 pt-1.5 text-xs text-slate-400">
            <span>Booking #{entry.booking_id}</span>
            <span>{fmtINR(entry.amount)} for this pup</span>
          </li>
        </ul>
      )}
    </li>
  );
};

export const PupHistoryModal = ({ pup, open, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && pup) {
      setLoading(true);
      api
        .get(`/pups/${pup.pup_id}/history`)
        .then((r) => setData(r.data))
        .catch(() => setData(null))
        .finally(() => setLoading(false));
    }
  }, [open, pup]);

  const summary = data?.summary;
  const parts = summary
    ? SUMMARY_LABELS.filter(([k]) => summary[k] > 0).map(([k, l]) => `${summary[k]} ${l}`)
    : [];
  const sentence = parts.length
    ? `${pup?.name} has ${parts.join(", ")} with us — ${fmtINR(summary.spent)} in services so far.`
    : `No services availed yet — ${pup?.name}'s history starts with the first booking.`;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="pup-history-modal" className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 font-display text-2xl font-medium text-ink">
            <span className="rounded-full bg-bark-soft p-2.5 text-bark">
              <History className="h-5 w-5" aria-hidden="true" />
            </span>
            {pup?.name}'s history
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <p className="py-10 text-center text-sm text-slate-400">Loading history…</p>
        ) : (
          <>
            <div data-testid="history-summary" className="rounded-2xl bg-cream-dark/60 p-5">
              <p className="text-sm leading-relaxed text-slate-700">{sentence}</p>
              {summary && parts.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {SUMMARY_LABELS.filter(([k]) => summary[k] > 0).map(([k, l]) => (
                    <span key={k} className="rounded-full bg-white px-3 py-1 text-[11px] font-semibold text-navy">
                      {summary[k]} {l}
                    </span>
                  ))}
                  <span className="rounded-full bg-navy px-3 py-1 text-[11px] font-semibold text-cream">
                    {fmtINR(summary.spent)} total
                  </span>
                </div>
              )}
            </div>

            <ul className="mt-5 space-y-3">
              {(data?.entries || []).map((e) => (
                <Entry key={e.booking_id} entry={e} />
              ))}
            </ul>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
